import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography, Alert } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faFileExcel,
    faFilePdf,
    faSyncAlt,
    faFilter,
    faCalendarAlt,
    faChartBar,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import TablePaginationActions from '../../../../base-components/TablePaginationActions';
import { isDynamicStaffRole } from '../../../../utils/roleAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FIXED_COL_COUNT = 4;
const MONTH_SUB_COL_COUNT = 3;

const analysisTableSx = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableCell-root': {
        borderRight: '1px solid #cbd5e1',
        borderBottom: '1px solid #cbd5e1',
    },
    '& .MuiTableBody-root .MuiTableCell-root, & .MuiTableFooter-root .MuiTableCell-root': {
        color: '#0f172a',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: '#033b51 !important',
        color: '#ffffff !important',
        borderRight: '1px solid rgba(255, 255, 255, 0.28)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.28)',
    },
    '& .fixed-group-end': {
        borderRight: '3px solid #033b51 !important',
    },
    '& .MuiTableHead-root .fixed-group-end': {
        borderRight: '3px solid rgba(255, 255, 255, 0.75) !important',
    },
    '& .month-group-start': {
        borderLeft: '3px solid #033b51 !important',
    },
    '& .MuiTableHead-root .month-group-start': {
        borderLeft: '3px solid rgba(255, 255, 255, 0.75) !important',
    },
    '& .MuiTableBody-root .month-group-alt, & .MuiTableFooter-root .month-group-alt': {
        backgroundColor: '#f8fafc',
        color: '#0f172a',
    },
    '& .MuiTableHead-root .month-group-alt': {
        backgroundColor: '#0a4a63 !important',
        color: '#ffffff !important',
    },
    '& .grand-total-col': {
        borderLeft: '3px solid #033b51 !important',
    },
    '& .MuiTableHead-root .grand-total-col': {
        borderLeft: '3px solid rgba(255, 255, 255, 0.75) !important',
    },
};

function defaultFromDate() {
    return dayjs().subtract(5, 'month').startOf('month').format('YYYY-MM-DD');
}

function defaultToDate() {
    return dayjs().endOf('month').format('YYYY-MM-DD');
}

function isMonthActive(cell) {
    return cell === 1 || cell?.active === 1;
}

function getMonthStart(cell) {
    if (!cell || typeof cell !== 'object') return '';
    return cell.startDate || '';
}

function getMonthEnd(cell) {
    if (!cell || typeof cell !== 'object') return '';
    return cell.endDate || '';
}

function formatDisplayDate(value) {
    if (!value) return '';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : value;
}

function formatMonthEndDisplay(cell) {
    if (!isMonthActive(cell)) return '';
    const end = getMonthEnd(cell);
    if (!end) return 'Ongoing';
    return formatDisplayDate(end);
}

function buildExportSheet(data) {
    const months = data.months || [];
    const colCount = FIXED_COL_COUNT + months.length * MONTH_SUB_COL_COUNT + 1;
    const aoa = [];

    aoa.push([data.title || 'Analysis of monthly membership', ...Array(colCount - 1).fill('')]);
    aoa.push([]);
    aoa.push([]);

    const headerTop = ['Member ID', 'Member Name', 'Phone', 'Email'];
    const headerSub = ['', '', '', ''];
    for (const month of months) {
        headerTop.push(month.label, '', '');
        headerSub.push('Active', 'Start', 'End');
    }
    headerTop.push('Grand Total');
    headerSub.push('');
    aoa.push(headerTop);
    aoa.push(headerSub);

    for (const row of data.rows || []) {
        const line = [
            row.memberPackageId,
            row.memberName || '',
            row.phone || '',
            row.email || '',
        ];
        for (const month of months) {
            const cell = row.months?.[month.key];
            line.push(
                isMonthActive(cell) ? 1 : '',
                getMonthStart(cell) ? formatDisplayDate(getMonthStart(cell)) : '',
                formatMonthEndDisplay(cell)
            );
        }
        line.push(row.grandTotal ?? '');
        aoa.push(line);
    }

    const totalRow = ['Grand Total', '', '', ''];
    for (const month of months) {
        totalRow.push(data.columnTotals?.[month.key] ?? 0, '', '');
    }
    totalRow.push(data.columnTotals?.grandTotal ?? 0);
    aoa.push(totalRow);

    return aoa;
}

function exportExcel(data, filename) {
    const aoa = buildExportSheet(data);
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function exportPdf(data, filename) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const months = data.months || [];

    doc.setFontSize(14);
    doc.text(data.title || 'Analysis of monthly membership', 40, 40);

    doc.setFontSize(10);
    const periodText = `Period: ${dayjs(data.period?.from_date).format('MMM DD, YYYY')} - ${dayjs(data.period?.to_date).format('MMM DD, YYYY')}`;
    doc.text(periodText, 40, 58);

    const head = [
        [
            'Member ID',
            'Member Name',
            'Phone',
            'Email',
            ...months.flatMap((m) => [m.label, 'Start', 'End']),
            'Grand Total',
        ],
    ];

    const body = (data.rows || []).map((row) => [
        row.memberPackageId,
        row.memberName || '',
        row.phone || '',
        row.email || '',
        ...months.flatMap((m) => {
            const cell = row.months?.[m.key];
            return [
                isMonthActive(cell) ? '1' : '',
                getMonthStart(cell) ? formatDisplayDate(getMonthStart(cell)) : '',
                formatMonthEndDisplay(cell),
            ];
        }),
        String(row.grandTotal ?? ''),
    ]);

    body.push([
        'Grand Total',
        '',
        '',
        '',
        ...months.flatMap((m) => [String(data.columnTotals?.[m.key] ?? 0), '', '']),
        String(data.columnTotals?.grandTotal ?? 0),
    ]);

    autoTable(doc, {
        startY: 72,
        head,
        body,
        theme: 'grid',
        styles: { fontSize: 6, cellPadding: 2, halign: 'center' },
        headStyles: { fillColor: [3, 59, 81], textColor: 255 },
        columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'left' },
            2: {halign: 'left' },
            3: {halign: 'left' },
        },
        margin: { left: 24, right: 24 },
        tableWidth: pageWidth - 48,
    });

    doc.save(`${filename}.pdf`);
}

function MembershipAnalysisReport() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [fetchError, setFetchError] = useState('');

    const [role, setRole] = useState('');
    const [location_id, setLocationID] = useState('');
    const [company_id, setCompanyID] = useState('');

    const [fromDate, setFromDate] = useState(defaultFromDate());
    const [toDate, setToDate] = useState(defaultToDate());
    const [searchQuery, setSearchQuery] = useState('');
    const [membershipStatus, setMembershipStatus] = useState('');

    const [title, setTitle] = useState('');
    const [months, setMonths] = useState([]);
    const [rows, setRows] = useState([]);
    const [columnTotals, setColumnTotals] = useState({});
    const [period, setPeriod] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        const sessionStr = sessionStorage.getItem('user-info');
        if (sessionStr) {
            const session = JSON.parse(sessionStr);
            setRole(session.user.role);
            setCompanyID(session.user.company_id);
            setLocationID(session.user.location_id);
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const buildParams = useCallback(
        (currentPage, currentLimit, currentSearch, currentFrom, currentTo, currentStatus) => {
            const params = new URLSearchParams();
            if (role === 'Admin') params.append('company_id', company_id);
            if (role === 'ClubAdmin') params.append('location_id', location_id);
            else if (isDynamicStaffRole(role)) params.append('location_id', location_id);
            params.append('page', String(currentPage + 1));
            params.append('limit', String(currentLimit));
            if (currentSearch) params.append('search', currentSearch);
            if (currentFrom) params.append('from_date', currentFrom);
            if (currentTo) params.append('to_date', currentTo);
            if (currentStatus) params.append('membership_status', currentStatus);
            return params;
        },
        [role, company_id, location_id]
    );

    const fetchReportData = useCallback(
        async (
            currentPage = page,
            currentLimit = rowsPerPage,
            currentSearch = searchQuery,
            currentFrom = fromDate,
            currentTo = toDate,
            currentStatus = membershipStatus
        ) => {
            if (!role) return;
            setLoading(true);
            setFetchError('');
            try {
                const params = buildParams(
                    currentPage,
                    currentLimit,
                    currentSearch,
                    currentFrom,
                    currentTo,
                    currentStatus
                );
                const response = await fetch(`${BASE_URL}reports/membership-analysis?${params.toString()}`);
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to load membership analysis report.');
                }
                setTitle(data.title);
                setMonths(data.months || []);
                setRows(data.rows || []);
                setColumnTotals(data.columnTotals || {});
                setPeriod(data.period || null);
                setTotalRows(data.totalRows ?? 0);
            } catch (error) {
                console.error('Error fetching membership analysis:', error);
                setFetchError(error.message || 'Failed to load membership analysis report.');
            } finally {
                setLoading(false);
            }
        },
        [role, page, rowsPerPage, searchQuery, fromDate, toDate, membershipStatus, buildParams]
    );

    useEffect(() => {
        if (role) {
            fetchReportData();
        }
    }, [role, page, rowsPerPage, fetchReportData]);

    const fetchAllForExport = async () => {
        const params = buildParams(0, 1000000, searchQuery, fromDate, toDate, membershipStatus);
        const response = await fetch(`${BASE_URL}reports/membership-analysis?${params.toString()}`);
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Export data fetch failed');
        }
        return data;
    };

    const handleFilter = () => {
        setPage(0);
        fetchReportData(0, rowsPerPage, searchQuery, fromDate, toDate, membershipStatus);
    };

    const handleClear = () => {
        const from = defaultFromDate();
        const to = defaultToDate();
        setFromDate(from);
        setToDate(to);
        setSearchQuery('');
        setMembershipStatus('');
        setPage(0);
        fetchReportData(0, rowsPerPage, '', from, to, '');
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const data = await fetchAllForExport();
            const stamp = dayjs().format('DD-MM-YY');
            exportExcel(data, `Membership_Analysis_${stamp}`);
        } catch (error) {
            console.error(error);
            setFetchError(error.message || 'Excel export failed.');
        } finally {
            setExporting(false);
        }
    };

    const handleExportPdf = async () => {
        setExporting(true);
        try {
            const data = await fetchAllForExport();
            const stamp = dayjs().format('DD-MM-YY');
            exportPdf(data, `Membership_Analysis_${stamp}`);
        } catch (error) {
            console.error(error);
            setFetchError(error.message || 'PDF export failed.');
        } finally {
            setExporting(false);
        }
    };

    const tableColSpan = FIXED_COL_COUNT + months.length * MONTH_SUB_COL_COUNT + 1;
    const headerCellClass = 'font-bold bg-gx-navy text-white';

    return (
      <>

        
            <Box className="p-4 md:p-6">
                <Box className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <Box>
                        <Typography variant="h4" className="font-bold text-gx-navy flex items-center gap-3">
                            <FontAwesomeIcon icon={faChartBar} />
                            Membership Analysis
                        </Typography>
                        <Typography className="text-gx-ink-soft mt-1">
                            Monthly membership matrix — active members per month (1 = active)
                        </Typography>
                    </Box>
                    <Box className="flex gap-2 flex-wrap">
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                            onClick={handleExportExcel}
                            disabled={exporting || loading}
                        >
                            Excel
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                            onClick={handleExportPdf}
                            disabled={exporting || loading}
                        >
                            PDF
                        </Button>
                    </Box>
                </Box>

                <Paper elevation={2} className="p-4 mb-6 rounded-xl">
                    <Box className="flex flex-wrap gap-4 items-end">
                        <Box>
                            <Typography className="text-sm font-semibold mb-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faCalendarAlt} /> From
                            </Typography>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="border border-gx-border rounded-md px-3 py-2"
                            />
                        </Box>
                        <Box>
                            <Typography className="text-sm font-semibold mb-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faCalendarAlt} /> To
                            </Typography>
                            <input
                                type="date"
                                value={toDate}
                                min={fromDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="border border-gx-border rounded-md px-3 py-2"
                            />
                        </Box>
                        <Box className="flex-grow min-w-[200px]">
                            <Typography className="text-sm font-semibold mb-1 flex items-center gap-1">
                                <FontAwesomeIcon icon={faSearch} /> Search
                            </Typography>
                            <input
                                type="text"
                                placeholder="Member ID, name, phone, or email"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border border-gx-border rounded-md px-3 py-2 w-full"
                            />
                        </Box>
                        <Box>
                            <Typography className="text-sm font-semibold mb-1">
                                Membership Status
                            </Typography>
                            <select
                                value={membershipStatus}
                                onChange={(e) => setMembershipStatus(e.target.value)}
                                className="border border-gx-border rounded-md px-3 py-2 min-w-[150px]"
                            >
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </Box>
                        <Button variant="contained" onClick={handleFilter} startIcon={<FontAwesomeIcon icon={faFilter} />}>
                            Apply
                        </Button>
                        <Button variant="outlined" onClick={handleClear}>
                            Clear
                        </Button>
                        <Button variant="text" onClick={() => fetchReportData()} startIcon={<FontAwesomeIcon icon={faSyncAlt} />}>
                            Refresh
                        </Button>
                    </Box>
                </Paper>

                {fetchError && (
                    <Alert severity="error" className="mb-4" onClose={() => setFetchError('')}>
                        {fetchError}
                    </Alert>
                )}

                {period && (
                    <Typography className="text-sm text-gx-ink-soft mb-3">
                        {title} · {dayjs(period.from_date).format('MMM YYYY')} – {dayjs(period.to_date).format('MMM YYYY')} · {totalRows} members
                    </Typography>
                )}

                {loading ? (
                    <Box className="flex justify-center py-20">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={2} className="rounded-xl overflow-auto">
                        <Table size="small" stickyHeader sx={analysisTableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell rowSpan={2} className={`${headerCellClass} sticky left-0 z-10 min-w-[120px]`}>
                                        Member ID
                                    </TableCell>
                                    <TableCell rowSpan={2} className={`${headerCellClass} min-w-[160px]`}>
                                        Member Name
                                    </TableCell>
                                    <TableCell rowSpan={2} className={`${headerCellClass} min-w-[120px]`}>
                                        Phone
                                    </TableCell>
                                    <TableCell rowSpan={2} className={`${headerCellClass} fixed-group-end min-w-[180px]`}>
                                        Email
                                    </TableCell>
                                    {months.map((month, monthIndex) => {
                                        const groupStart = monthIndex > 0 ? ' month-group-start' : '';
                                        const altClass = monthIndex % 2 === 1 ? ' month-group-alt' : '';
                                        return (
                                            <TableCell
                                                key={month.key}
                                                colSpan={3}
                                                align="center"
                                                className={`${headerCellClass}${groupStart} whitespace-nowrap${altClass}`}
                                            >
                                                {month.label}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell rowSpan={2} align="center" className={`${headerCellClass} grand-total-col min-w-[90px]`}>
                                        Grand Total
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    {months.map((month, monthIndex) => {
                                        const groupStart = monthIndex > 0 ? ' month-group-start' : '';
                                        const altClass = monthIndex % 2 === 1 ? ' month-group-alt' : '';
                                        return (
                                            <React.Fragment key={`${month.key}-sub`}>
                                                <TableCell align="center" className={`${headerCellClass}${groupStart} min-w-[60px]${altClass}`}>
                                                    Active
                                                </TableCell>
                                                <TableCell align="center" className={`${headerCellClass} min-w-[110px]${altClass}`}>
                                                    Start
                                                </TableCell>
                                                <TableCell align="center" className={`${headerCellClass} min-w-[110px]${altClass}`}>
                                                    End
                                                </TableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={tableColSpan} align="center" className="py-10 text-gx-ink-soft">
                                            No records found for the selected period.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow hover key={row.memberPackageId}>
                                            <TableCell className="font-medium sticky left-0 bg-white z-[1]">
                                                {row.memberPackageId}
                                            </TableCell>
                                            <TableCell>{row.memberName || ''}</TableCell>
                                            <TableCell>{row.phone || ''}</TableCell>
                                            <TableCell className="fixed-group-end">{row.email || ''}</TableCell>
                                            {months.map((month, monthIndex) => {
                                                const cell = row.months?.[month.key];
                                                const groupStart = monthIndex > 0 ? 'month-group-start' : '';
                                                const altClass = monthIndex % 2 === 1 ? 'month-group-alt' : '';
                                                const cellClass = [groupStart, altClass].filter(Boolean).join(' ');
                                                return (
                                                    <React.Fragment key={month.key}>
                                                        <TableCell align="center" className={cellClass}>
                                                            {isMonthActive(cell) ? 1 : ''}
                                                        </TableCell>
                                                        <TableCell align="center" className={`whitespace-nowrap text-xs${altClass ? ` ${altClass}` : ''}`}>
                                                            {getMonthStart(cell) ? formatDisplayDate(getMonthStart(cell)) : ''}
                                                        </TableCell>
                                                        <TableCell align="center" className={`whitespace-nowrap text-xs${altClass ? ` ${altClass}` : ''}`}>
                                                            {formatMonthEndDisplay(cell)}
                                                        </TableCell>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <TableCell align="center" className="font-semibold grand-total-col">
                                                {row.grandTotal}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            {rows.length > 0 && (
                                <TableFooter>
                                    <TableRow className="bg-gx-canvas">
                                        <TableCell className="font-bold sticky left-0 bg-gx-canvas z-[1]">
                                            Grand Total
                                        </TableCell>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell className="fixed-group-end" />
                                        {months.map((month, monthIndex) => {
                                            const groupStart = monthIndex > 0 ? 'month-group-start' : '';
                                            const altClass = monthIndex % 2 === 1 ? 'month-group-alt' : '';
                                            const activeClass = ['font-bold', groupStart, altClass].filter(Boolean).join(' ');
                                            return (
                                                <React.Fragment key={`total-${month.key}`}>
                                                    <TableCell align="center" className={activeClass}>
                                                        {columnTotals[month.key] ?? 0}
                                                    </TableCell>
                                                    <TableCell className={altClass} />
                                                    <TableCell className={altClass} />
                                                </React.Fragment>
                                            );
                                        })}
                                        <TableCell align="center" className="font-bold grand-total-col">
                                            {columnTotals.grandTotal ?? 0}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            colSpan={tableColSpan}
                                            count={totalRows}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={(_, newPage) => setPage(newPage)}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            ActionsComponent={TablePaginationActions}
                                        />
                                    </TableRow>
                                </TableFooter>
                            )}
                        </Table>
                    </TableContainer>
                )}
            </Box>
        
      </>
    );
}

export default MembershipAnalysisReport;
